import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import CardComp from "shared/components/card";
import Chart from "shared/components/chart";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import { getAnalyticsActivity } from "shared/services/productManagerService";
import styles from "./style.module.scss";

function AppAnalyticsActivity() {
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>({
    startDate: moment().subtract(30, "days").toDate(),
    endDate: new Date(),
  });
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useBreadcrumbs([
    {
      label: routeConstant.dashboard.title,
      route: routeConstant.dashboard.path,
    },
    {
      label: routeConstant.appAnalytics.title,
      route: routeConstant.appAnalytics.path,
    },
    {
      label: routeConstant.appAnalyticsActivity.title,
      route: routeConstant.appAnalyticsActivity.path,
    },
  ]);

  const onChangeDate = (dates: any) => {
    const [start, end] = dates;
    setDateRange({
      startDate: start,
      endDate: end,
    });
  };

  const fetchActivityData = () => {
    setLoading(true);
    const dateFrom = dateRange.startDate
      ? moment(dateRange.startDate).format("YYYY-MM-DD")
      : undefined;
    const dateTo = dateRange.endDate
      ? moment(dateRange.endDate).format("YYYY-MM-DD")
      : undefined;

    getAnalyticsActivity(dateFrom, dateTo)
      .then(({ data }) => {
        setAnalyticsData(data?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("error", err?.response?.data?.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (dateRange.endDate) {
      fetchActivityData();
    }
  }, [dateRange]);

  const activityCards = [
    {
      title: "Last 1 Day",
      value: analyticsData?.active_users?.last_1_day || 0,
    },
    {
      title: "Last 7 Days",
      value: analyticsData?.active_users?.last_7_days || 0,
    },
    {
      title: "Last 30 Days",
      value: analyticsData?.active_users?.last_30_days || 0,
    },
    {
      title: "Inactive 30+ Days",
      value: analyticsData?.active_users?.inactive_30_plus_days || 0,
    },
  ];

  const chartData = {
    labels: analyticsData?.daily_active_users?.map((item: any) => item.date) || [],
    datasets: [
      {
        label: "Daily Active Users",
        data: analyticsData?.daily_active_users?.map((item: any) => item.count) || [],
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        tension: 0.1,
      },
    ],
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4">User Activity Analytics</Typography>
        <Box className={styles.datePicker}>
          <DatePicker
            selected={dateRange.startDate}
            onChange={onChangeDate}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            selectsRange
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date range"
            className={styles.dateInput}
          />
        </Box>
      </Box>

      <Grid container spacing={3} className={styles.statsContainer}>
        {activityCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <CardComp
              title={card.title}
              stats={card.value}
              isLoading={loading}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} className={styles.chartContainer}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Active Users Trend
              </Typography>
              {analyticsData?.daily_active_users && (
                <Chart
                  labels={chartData.labels}
                  sales={chartData.datasets[0].data}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AppAnalyticsActivity;
